/** Maintained deterministic match puzzle. The controller owns swaps, match
 * detection, gravity, bounded cyclic refill, cascade scoring and terminal
 * objective/resource clocks. It has no host, DOM, wall clock or RNG. */
export type MatchPuzzleVariantV1='standard'|'alternate';
export interface MatchPuzzleCellV1{col:number;row:number}
export type MatchPuzzleCommandV1={type:'swap';from:MatchPuzzleCellV1;to:MatchPuzzleCellV1};
export interface MatchPuzzleConfigV1{
  profile:'match-puzzle@1';variant:MatchPuzzleVariantV1;
  board:{columns:number;rows:number;kinds:number;cells:number[][];refill:number[]};
  rules:{moves:number;timeLimitTicks:number;targetCleared:number;scorePerGem:number};
}
export interface MatchPuzzleSnapshotV1{
  tick:number;variant:MatchPuzzleVariantV1;columns:number;rows:number;kinds:number;grid:number[][];
  movesUsed:number;movesLeft:number;cleared:number;targetCleared:number;combo:number;lastGained:number;refillCursor:number;
  score:number;timeLeft:number;phase:'active'|'won'|'lost';lossCause:null|'moves'|'time';
}
export type MatchPuzzleEventV1=
  |{kind:'swap';tick:number;data:{slot:number;from:MatchPuzzleCellV1;to:MatchPuzzleCellV1;movesLeft:number}}
  |{kind:'bad-swap';tick:number;data:{slot:number;from:MatchPuzzleCellV1;to:MatchPuzzleCellV1;reason:'no-match'}}
  |{kind:'match';tick:number;data:{slot:number;cascade:number;count:number;gained:number;x:number;y:number}}
  |{kind:'cascade';tick:number;data:{slot:number;cascade:number}}
  |{kind:'refill';tick:number;data:{slot:number;cascade:number;count:number}}
  |{kind:'won';tick:number;data:{slot:number;score:number;cleared:number}}
  |{kind:'lost';tick:number;data:{slot:number;cause:'moves'|'time';score:number;cleared:number}};

const STANDARD_BOARD=[[4,1,3,2,3],[2,1,2,3,1],[3,3,2,3,4],[4,3,4,4,3],[2,2,1,3,3]];
const STANDARD_REFILL=[3,3,4,1,4,1,4,3,2,3,1,2,2,3,4,4,1,4,4,3,4,1,1,4,2,2,3,1,2,1,2,3];
const ALTERNATE_BOARD=[[4,4,3,4,3],[4,1,4,3,1],[3,4,3,2,3],[4,2,2,1,1],[1,2,2,1,3]];
const ALTERNATE_REFILL=[4,2,1,2,2,1,2,4,4,2,1,2,3,4,4,1,2,3,4,1,3,1,3,4,4,3,1,4,1,2,4,3];

export function matchPuzzleConfigV1(variant:MatchPuzzleVariantV1='standard'):MatchPuzzleConfigV1{
  if(variant!=='standard'&&variant!=='alternate')throw new RangeError('Unsupported match-puzzle variant');
  return{profile:'match-puzzle@1',variant,board:{columns:5,rows:5,kinds:4,
    cells:structuredClone(variant==='standard'?STANDARD_BOARD:ALTERNATE_BOARD),refill:[...(variant==='standard'?STANDARD_REFILL:ALTERNATE_REFILL)]},
    rules:{moves:3,timeLimitTicks:300,targetCleared:20,scorePerGem:10}};
}

function record(value:unknown,keys:readonly string[]):asserts value is Record<string,unknown>{
  if(!value||typeof value!=='object'||Array.isArray(value)||![Object.prototype,null].includes(Object.getPrototypeOf(value))
    ||Reflect.ownKeys(value).length!==keys.length||Object.keys(value).sort().join(',')!==[...keys].sort().join(',')
    ||Object.values(Object.getOwnPropertyDescriptors(value)).some(d=>!('value'in d)))throw new RangeError('Invalid match-puzzle record');
}
function integer(value:unknown,min:number,max:number):asserts value is number{
  if(typeof value!=='number'||!Number.isSafeInteger(value)||value<min||value>max)throw new RangeError('Invalid match-puzzle integer');
}
function matches(grid:readonly (readonly number[])[],columns:number,rows:number):MatchPuzzleCellV1[]{
  const marked=new Set<string>(),mark=(col:number,row:number)=>marked.add(`${col},${row}`);
  for(let row=0;row<rows;row++){
    let run=1;for(let col=1;col<=columns;col++){
      const same=col<columns&&grid[row]![col]===grid[row]![col-1];
      if(same)run++;else{if(run>=3)for(let cursor=col-run;cursor<col;cursor++)mark(cursor,row);run=1;}
    }
  }
  for(let col=0;col<columns;col++){
    let run=1;for(let row=1;row<=rows;row++){
      const same=row<rows&&grid[row]![col]===grid[row-1]![col];
      if(same)run++;else{if(run>=3)for(let cursor=row-run;cursor<row;cursor++)mark(col,cursor);run=1;}
    }
  }
  return[...marked].map(key=>{const[col,row]=key.split(',').map(Number);return{col:col!,row:row!};}).sort((a,b)=>a.row-b.row||a.col-b.col);
}

export function validateMatchPuzzleConfigV1(input:unknown):MatchPuzzleConfigV1{
  record(input,['profile','variant','board','rules']);
  if(input.profile!=='match-puzzle@1'||!['standard','alternate'].includes(String(input.variant)))throw new RangeError('Unsupported match-puzzle profile');
  record(input.board,['columns','rows','kinds','cells','refill']);integer(input.board.columns,4,8);integer(input.board.rows,4,8);integer(input.board.kinds,3,8);
  if(!Array.isArray(input.board.cells)||input.board.cells.length!==input.board.rows)throw new RangeError('Invalid match-puzzle board rows');
  for(const row of input.board.cells){
    if(!Array.isArray(row)||row.length!==input.board.columns)throw new RangeError('Invalid match-puzzle board columns');
    for(const value of row)integer(value,1,input.board.kinds);
  }
  if(matches(input.board.cells,input.board.columns,input.board.rows).length)throw new RangeError('Match-puzzle board starts resolved');
  if(!Array.isArray(input.board.refill)||input.board.refill.length<8||input.board.refill.length>256)throw new RangeError('Invalid match-puzzle refill source');
  for(const value of input.board.refill)integer(value,1,input.board.kinds);
  record(input.rules,['moves','timeLimitTicks','targetCleared','scorePerGem']);integer(input.rules.moves,1,20);
  integer(input.rules.timeLimitTicks,1,18000);integer(input.rules.targetCleared,3,input.board.columns*input.board.rows*20);integer(input.rules.scorePerGem,1,10000);
  return structuredClone(input)as unknown as MatchPuzzleConfigV1;
}

export function createMatchPuzzleV1(input:MatchPuzzleConfigV1){
  const config=validateMatchPuzzleConfigV1(input),{board,rules}=config;
  let grid=board.cells.map(row=>[...row]),tick=0,movesUsed=0,cleared=0,score=0,combo=0,lastGained=0,refillCursor=0;
  let phase:MatchPuzzleSnapshotV1['phase']='active',lossCause:MatchPuzzleSnapshotV1['lossCause']=null;
  let pending:MatchPuzzleCommandV1|null=null,events:MatchPuzzleEventV1[]=[];
  const inBounds=(cell:MatchPuzzleCellV1)=>cell.col>=0&&cell.row>=0&&cell.col<board.columns&&cell.row<board.rows;
  const swap=(a:MatchPuzzleCellV1,b:MatchPuzzleCellV1)=>{const value=grid[a.row]![a.col]!;grid[a.row]![a.col]=grid[b.row]![b.col]!;grid[b.row]![b.col]=value;};
  const lose=(cause:'moves'|'time')=>{phase='lost';lossCause=cause;events.push({kind:'lost',tick,data:{slot:0,cause,score,cleared}});};
  const collapse=(removed:readonly MatchPuzzleCellV1[]):number=>{
    const gone=new Set(removed.map(cell=>`${cell.col},${cell.row}`));let count=0;
    for(let col=0;col<board.columns;col++){
      const survivors:number[]=[];for(let row=0;row<board.rows;row++)if(!gone.has(`${col},${row}`))survivors.push(grid[row]![col]!);
      const needed=board.rows-survivors.length,values:number[]=[];count+=needed;
      for(let index=0;index<needed;index++){values.push(board.refill[refillCursor%board.refill.length]!);refillCursor++;}
      values.push(...survivors);for(let row=0;row<board.rows;row++)grid[row]![col]=values[row]!;
    }return count;
  };
  const resolve=(command:MatchPuzzleCommandV1)=>{
    swap(command.from,command.to);let found=matches(grid,board.columns,board.rows);
    if(!found.length){swap(command.from,command.to);combo=0;lastGained=0;events.push({kind:'bad-swap',tick,data:{slot:0,from:{...command.from},to:{...command.to},reason:'no-match'}});return;}
    movesUsed++;events.push({kind:'swap',tick,data:{slot:0,from:{...command.from},to:{...command.to},movesLeft:Math.max(0,rules.moves-movesUsed)}});
    combo=0;lastGained=0;
    while(found.length){
      combo++;if(combo>64)throw new Error('Match-puzzle cascade exceeded maintained bound');
      if(combo>1)events.push({kind:'cascade',tick,data:{slot:0,cascade:combo}});
      let x=0,y=0;for(const cell of found){x+=cell.col+.5;y+=cell.row+.5;}
      const gained=found.length*rules.scorePerGem*combo;score+=gained;lastGained+=gained;cleared+=found.length;
      events.push({kind:'match',tick,data:{slot:0,cascade:combo,count:found.length,gained,x:x/found.length,y:y/found.length}});
      const count=collapse(found);events.push({kind:'refill',tick,data:{slot:0,cascade:combo,count}});
      found=matches(grid,board.columns,board.rows);
    }
    if(cleared>=rules.targetCleared){phase='won';events.push({kind:'won',tick,data:{slot:0,score,cleared}});}
    else if(movesUsed>=rules.moves)lose('moves');
  };
  return{
    applyCommand(slot:number,command:unknown):boolean{
      if(slot!==0||phase!=='active')return false;
      try{record(command,['type','from','to']);record(command.from,['col','row']);record(command.to,['col','row']);}catch{return false;}
      if(command.type!=='swap'||!Number.isSafeInteger(command.from.col)||!Number.isSafeInteger(command.from.row)||!Number.isSafeInteger(command.to.col)||!Number.isSafeInteger(command.to.row))return false;
      const from={col:command.from.col as number,row:command.from.row as number},to={col:command.to.col as number,row:command.to.row as number};
      if(!inBounds(from)||!inBounds(to)||Math.abs(from.col-to.col)+Math.abs(from.row-to.row)!==1)return false;
      pending={type:'swap',from,to};return true;
    },
    step():void{
      tick++;if(phase!=='active'){pending=null;return;}
      const command=pending;pending=null;if(command)resolve(command);
      if(phase==='active'&&tick>=rules.timeLimitTicks)lose('time');
    },
    snapshot():MatchPuzzleSnapshotV1{return structuredClone({tick,variant:config.variant,columns:board.columns,rows:board.rows,kinds:board.kinds,grid,
      movesUsed,movesLeft:Math.max(0,rules.moves-movesUsed),cleared,targetCleared:rules.targetCleared,combo,lastGained,refillCursor,score,
      timeLeft:Math.max(0,rules.timeLimitTicks-tick),phase,lossCause});},
    drainEvents():MatchPuzzleEventV1[]{const out=events;events=[];return out;},
  };
}
